import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { isRecord } from '@revelio/core/is-record';

import { openReplayStateLogger } from '../../hooks/open-replay';
import type {
  GlobalAssistantReportParamSuggestion,
  GlobalAssistantReportPrefillMetrics,
} from './global-ai-assistant-api';

type GlobalAssistantReportLaunchPendingState = {
  promotedTemplateId: string | null;
  reportType: string | null;
  paramSuggestions: GlobalAssistantReportParamSuggestion[];
  mwPrefillSuggestions: GlobalAssistantReportParamSuggestion[];
  mwPrefillLaunchSeq: number;
  prefillMetrics: GlobalAssistantReportPrefillMetrics | null;
  launchSeq: number;
};

type GlobalAssistantReportLaunchPendingActions = {
  setPromotedLaunch: (payload: {
    promotedTemplateId: string;
    reportType: string;
    paramSuggestions: GlobalAssistantReportParamSuggestion[];
  }) => void;
  setMwReportPrefill: (payload: {
    reportType: string;
    paramSuggestions: GlobalAssistantReportParamSuggestion[];
  }) => void;
  bumpMwReportLaunchSeq: () => void;
  clearMwReportPrefill: () => void;
  clearPromotedLaunch: (metrics?: GlobalAssistantReportPrefillMetrics) => void;
};

type GlobalAssistantReportLaunchPendingStore =
  GlobalAssistantReportLaunchPendingState & {
    actions: GlobalAssistantReportLaunchPendingActions;
  };

const initialState: GlobalAssistantReportLaunchPendingState = {
  promotedTemplateId: null,
  reportType: null,
  paramSuggestions: [],
  mwPrefillSuggestions: [],
  mwPrefillLaunchSeq: 0,
  prefillMetrics: null,
  launchSeq: 0,
};

export const useGlobalAssistantReportLaunchPendingStore =
  create<GlobalAssistantReportLaunchPendingStore>()(
    devtools(
      openReplayStateLogger(
        (set) => ({
          ...initialState,
          actions: {
            setPromotedLaunch: (payload) =>
              set((state) => ({
                promotedTemplateId: payload.promotedTemplateId,
                reportType: payload.reportType,
                paramSuggestions: payload.paramSuggestions,
                mwPrefillSuggestions: [],
                prefillMetrics: null,
                launchSeq: state.launchSeq + 1,
              })),
            setMwReportPrefill: (payload) =>
              set((state) => ({
                ...state,
                reportType: payload.reportType,
                mwPrefillSuggestions: payload.paramSuggestions,
                mwPrefillLaunchSeq: state.mwPrefillLaunchSeq + 1,
              })),
            bumpMwReportLaunchSeq: () =>
              set((state) => ({
                ...state,
                mwPrefillLaunchSeq: state.mwPrefillLaunchSeq + 1,
              })),
            clearMwReportPrefill: () =>
              set((state) => ({
                ...state,
                mwPrefillSuggestions: [],
                mwPrefillLaunchSeq: state.mwPrefillLaunchSeq,
              })),
            clearPromotedLaunch: (metrics) =>
              set((state) => ({
                promotedTemplateId: null,
                reportType: null,
                paramSuggestions: [],
                mwPrefillSuggestions: [],
                prefillMetrics: metrics ?? null,
                launchSeq: state.launchSeq,
              })),
          },
        }),
        'global_assistant_report_launch_pending'
      ),
      { name: 'GlobalAssistantReportLaunchPending' }
    )
  );

/**
 * The history state of the navigation that delivers a promoted launch: the
 * report builder only takes a launch that brought it to the page.
 */
export const promotedLaunchNavigationState = (launchSeq: number) => ({
  promotedLaunchSeq: launchSeq,
});

export const readPromotedLaunchSeq = (state: unknown): number | null =>
  isRecord(state) && typeof state.promotedLaunchSeq === 'number'
    ? state.promotedLaunchSeq
    : null;

export const globalAssistantReportLaunchPendingStore = {
  getState: useGlobalAssistantReportLaunchPendingStore.getState,
  subscribe: useGlobalAssistantReportLaunchPendingStore.subscribe,
  get actions() {
    return useGlobalAssistantReportLaunchPendingStore.getState().actions;
  },
};
